import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAPI } from '../contexts/APIContext';
import ChallengeCompletionModal from '../components/weekly-challenge/ChallengeCompletionModal';
import { useAnalytics, usePageTracking } from '../hooks/useAnalytics';

const starPositions = [
  { top: '8%', left: '12%', size: 'h-1.5 w-1.5' },
  { top: '14%', left: '76%', size: 'h-2 w-2' },
  { top: '24%', left: '88%', size: 'h-1 w-1' },
  { top: '32%', left: '8%', size: 'h-1.5 w-1.5' },
  { top: '46%', left: '92%', size: 'h-2 w-2' },
  { top: '56%', left: '14%', size: 'h-1 w-1' },
  { top: '70%', left: '82%', size: 'h-1.5 w-1.5' },
  { top: '86%', left: '18%', size: 'h-1 w-1' },
];

const fallbackStories = [
  { id: 'fallback-1', title: 'Dragon Forest Quest', thumbnail: 'https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?auto=format&fit=crop&w=600&q=80' },
  { id: 'fallback-2', title: 'Moonlight Castle Mystery', thumbnail: 'https://images.unsplash.com/photo-1534447677768-be436bb09401?auto=format&fit=crop&w=600&q=80' },
  { id: 'fallback-3', title: 'Pirate Sky Adventure', thumbnail: 'https://images.unsplash.com/photo-1501426026826-31c667bdf23d?auto=format&fit=crop&w=600&q=80' },
];

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const getRemainingDays = (endDate) => {
  if (!endDate) return 0;
  const diff = new Date(endDate).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / MS_PER_DAY));
};

const getStoryStatus = (storyIndex, progress, totalStories) => {
  const safe = Math.max(0, Math.min(progress, totalStories));
  if (safe > storyIndex) return 'completed';
  if (safe === storyIndex && safe < totalStories) return 'current';
  return 'locked';
};

const WeeklyChallengePage = () => {
  usePageTracking('Weekly Challenge');
  const { trackWeeklyChallengeViewed, trackWeeklyChallengeStoryClicked } = useAnalytics();
  const { apiClient } = useAPI();
  const navigate = useNavigate();
  const [challengeData, setChallengeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  const loadChallenge = useCallback(async () => {
    const response = await apiClient.getWeeklyChallenge();
    if (!response || response.success === false || response.status === false) throw new Error(response?.error || 'Failed to load');
    return response?.data || response;
  }, [apiClient]);

  useEffect(() => {
    let mounted = true;
    const fetch = async ({ silent = false } = {}) => {
      if (!silent) setIsLoading(true);
      try {
        const data = await loadChallenge();
        if (mounted) { setChallengeData(data); setFetchError(''); trackWeeklyChallengeViewed(data?.theme, data?.remaining_days); }
      } catch (e) { if (mounted) setFetchError(e.message); }
      finally { if (mounted && !silent) setIsLoading(false); }
    };
    fetch();
    const id = setInterval(() => fetch({ silent: true }), 30000);
    return () => { mounted = false; clearInterval(id); };
  }, [loadChallenge]);

  useEffect(() => {
    const handler = (e) => e?.detail && setChallengeData(e.detail);
    window.addEventListener("weeklyChallenge:updated", handler);
    return () => window.removeEventListener("weeklyChallenge:updated", handler);
  }, []);

  useEffect(() => {
    try { const c = sessionStorage.getItem("weeklyChallenge:latest"); if (c) setChallengeData(JSON.parse(c)); } catch {}
  }, []);

  useEffect(() => {
    if (!challengeData) return;
    const completed = Number(challengeData?.stories_completed ?? 0);
    if (completed >= 3) {
      const key = `weeklyChallenge:completed:${challengeData?.start_date}`;
      if (!sessionStorage.getItem(key)) { setShowCompletionModal(true); sessionStorage.setItem(key, 'true'); }
    }
  }, [challengeData]);

  const stories = useMemo(() => {
    const backend = Array.isArray(challengeData?.stories) ? challengeData.stories : [];
    const merged = [...backend];
    while (merged.length < 3) merged.push(fallbackStories[merged.length]);
    return merged.slice(0, 3);
  }, [challengeData]);

  const safeProgress = useMemo(() => {
    const p = Number(challengeData?.stories_completed ?? challengeData?.progress ?? 0);
    return Math.max(0, Math.min(Number.isNaN(p) ? 0 : p, stories.length || 3));
  }, [challengeData, stories.length]);

  const remainingDays = useMemo(() => getRemainingDays(challengeData?.end_date), [challengeData?.end_date]);
  const rewardStars = challengeData?.reward_stars ?? 50;
  const challengeTheme = challengeData?.theme || 'Weekly Challenge';
  const progressPct = Math.round((safeProgress / (stories.length || 3)) * 100);
  const circumference = 2 * Math.PI * 60;
  const strokeOffset = circumference - (progressPct / 100) * circumference;

  const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

  return (
    <div
      className="min-h-screen relative overflow-hidden pt-28 pb-16 px-4 sm:px-6"
      style={{
        background: 'linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)',
        fontFamily: "'Inter', 'Fredoka', sans-serif",
      }}
    >
      {/* Soft decorative blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div style={{ position: 'absolute', top: -120, right: -120, width: 360, height: 360, borderRadius: '50%', background: 'rgba(0,101,248,0.05)', filter: 'blur(20px)' }} />
        <div style={{ position: 'absolute', bottom: -120, left: -120, width: 360, height: 360, borderRadius: '50%', background: 'rgba(0,202,255,0.06)', filter: 'blur(20px)' }} />
        {starPositions.map((star, idx) => (
          <motion.span
            key={idx}
            className={`absolute ${star.size} rounded-full`}
            style={{ top: star.top, left: star.left, background: 'rgba(0,101,248,0.18)' }}
            animate={{ opacity: [0.2, 0.7, 0.2], scale: [0.8, 1.2, 0.8] }}
            transition={{ duration: 2.5 + idx * 0.3, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
      </div>

      <div className="relative max-w-5xl mx-auto">
        {/* Hero Header */}
        <motion.header
          className="relative mb-10 overflow-hidden p-8 md:p-10"
          style={{
            background: '#FFFFFF',
            border: '2px solid rgba(11,26,51,0.06)',
            borderRadius: 28,
            boxShadow: '0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.08)',
          }}
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        >
          <div className="relative z-10">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 flex items-center justify-center"
                  style={{ background: 'rgba(0,101,248,0.10)', borderRadius: 14 }}
                >
                  <img src="/assets/pixley_stars.png" alt="" className="w-6 h-6" />
                </div>
                <h1 className="text-3xl md:text-4xl tracking-tight" style={{ ...FREDOKA, color: '#0B1A33', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '-0.01em' }}>
                  {challengeTheme}
                </h1>
              </div>
              <motion.div
                className="px-5 py-2 flex items-center gap-2"
                style={{
                  background: '#FFFFFF',
                  border: '2px solid rgba(0,101,248,0.18)',
                  borderRadius: 999,
                  boxShadow: '0px 3px 0px rgba(11,26,51,0.06)',
                }}
                animate={{ scale: [1, 1.02, 1] }} transition={{ duration: 2.4, repeat: Infinity }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0065F8" strokeWidth="2.4">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" strokeLinecap="round" />
                </svg>
                <span className="text-xs font-bold tracking-widest uppercase" style={{ ...FREDOKA, color: '#0065F8' }}>
                  Ends in: {remainingDays} days
                </span>
              </motion.div>
            </div>
            <p className="text-lg max-w-2xl" style={{ ...FREDOKA, color: '#334155' }}>
              Complete all 3 stories this week to earn{' '}
              <span style={{ color: '#0065F8', fontWeight: 700 }}>{rewardStars} stars!</span>
            </p>
          </div>
        </motion.header>

        {/* Main Grid: Stories + Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Story Cards */}
          <div className="lg:col-span-2 space-y-5">
            <h2 className="text-xs uppercase tracking-[0.2em] mb-4" style={{ ...FREDOKA, color: '#0065F8', fontWeight: 800 }}>
              Current Quests
            </h2>

            {isLoading ? (
              <motion.div
                className="py-12 text-center"
                style={{ ...FREDOKA, color: '#5A6B85' }}
                animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.5, repeat: Infinity }}
              >
                Loading challenge...
              </motion.div>
            ) : (
              stories.map((story, index) => {
                const status = getStoryStatus(index, safeProgress, stories.length);
                const title = story.name || story.title;
                const thumb = story.thumbnail_url || story.thumbnail || fallbackStories[index]?.thumbnail;
                const isLocked = status === 'locked';
                const isCurrent = status === 'current';
                const isCompleted = status === 'completed';

                return (
                  <motion.div
                    key={story.id || `story-${index}`}
                    className="group relative p-5 flex flex-col md:flex-row gap-5 transition-all duration-200"
                    style={{
                      background: '#FFFFFF',
                      border: isCurrent
                        ? '2px solid #0065F8'
                        : '2px solid rgba(11,26,51,0.06)',
                      borderRadius: 22,
                      boxShadow: isCurrent
                        ? '0px 4px 0px rgba(0,101,248,0.15), 0px 12px 28px rgba(0,101,248,0.10)'
                        : '0px 2px 0px rgba(11,26,51,0.04), 0px 4px 12px rgba(11,26,51,0.04)',
                      opacity: isLocked ? 0.85 : 1,
                    }}
                    initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                  >
                    {/* Thumbnail */}
                    <div
                      className="w-full md:w-44 h-44 shrink-0 relative overflow-hidden"
                      style={{ borderRadius: 16, background: '#EAF3FF' }}
                    >
                      <img
                        src={thumb}
                        alt={title}
                        className={`w-full h-full object-cover ${isLocked ? '' : 'group-hover:scale-105 transition-transform duration-500'}`}
                        style={{ filter: isLocked ? 'grayscale(1)' : 'none', opacity: isLocked ? 0.55 : 1 }}
                      />
                      {isLocked && (
                        <div
                          className="absolute inset-0 flex items-center justify-center"
                          style={{ background: 'rgba(11,26,51,0.18)' }}
                        >
                          <div
                            className="w-12 h-12 flex items-center justify-center"
                            style={{
                              background: '#FFFFFF',
                              borderRadius: 14,
                              boxShadow: '0px 3px 0px rgba(11,26,51,0.10)',
                            }}
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5A6B85" strokeWidth="2.4">
                              <rect x="5" y="11" width="14" height="9" rx="2" />
                              <path d="M8 11V8a4 4 0 018 0v3" strokeLinecap="round" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span
                            className="px-3 py-1 text-[10px] uppercase tracking-widest"
                            style={{
                              ...FREDOKA,
                              fontWeight: 800,
                              borderRadius: 999,
                              background: isCurrent
                                ? '#0065F8'
                                : isCompleted
                                  ? '#10B981'
                                  : '#D7E3F3',
                              color: isCurrent || isCompleted ? '#FFFFFF' : '#5A6B85',
                              boxShadow: isCurrent
                                ? '0px 2px 0px #073B8E'
                                : isCompleted
                                  ? '0px 2px 0px #047857'
                                  : 'none',
                            }}
                          >
                            {isCompleted ? '✓ Completed' : isCurrent ? 'In Progress' : 'Locked'}
                          </span>
                        </div>
                        <h3
                          className="text-xl mb-2"
                          style={{ ...FREDOKA, fontWeight: 700, color: isLocked ? '#8CA0BC' : '#0B1A33' }}
                        >
                          {title}
                        </h3>
                        {isLocked && index > 0 && (
                          <p className="text-sm" style={{ ...FREDOKA, color: '#8CA0BC' }}>
                            Complete the previous quest to unlock.
                          </p>
                        )}
                      </div>
                      {isCurrent && (
                        <div className="mt-4 flex items-center justify-between gap-4">
                          <div
                            className="flex-1 max-w-[200px] h-2 overflow-hidden"
                            style={{ background: 'rgba(11,26,51,0.08)', borderRadius: 999 }}
                          >
                            <div
                              className="h-full"
                              style={{
                                width: '40%',
                                background: 'linear-gradient(90deg, #0065F8 0%, #00CAFF 100%)',
                                borderRadius: 999,
                              }}
                            />
                          </div>
                          <button
                            onClick={() => { if (story.id && !story.id.startsWith('fallback')) { trackWeeklyChallengeStoryClicked(story.id); navigate(`/stories/${story.id}?fromWeeklyChallenge=1`); } }}
                            className="px-6 py-2.5 flex items-center gap-2 transition-all active:scale-95"
                            style={{
                              ...FREDOKA,
                              background: '#0065F8',
                              color: '#FFFFFF',
                              fontWeight: 700,
                              fontSize: 14,
                              borderRadius: 999,
                              border: 'none',
                              boxShadow: '0px 4px 0px #073B8E',
                              cursor: 'pointer',
                            }}
                          >
                            ▶ Play
                          </button>
                        </div>
                      )}
                      {isCompleted && (
                        <div className="mt-4">
                          <div
                            className="h-2 overflow-hidden"
                            style={{ background: 'rgba(16,185,129,0.20)', borderRadius: 999 }}
                          >
                            <div className="h-full" style={{ width: '100%', background: '#10B981', borderRadius: 999 }} />
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })
            )}

            {fetchError && (
              <p className="text-sm mt-2" style={{ ...FREDOKA, color: '#DC2626' }}>{fetchError}</p>
            )}
          </div>

          {/* Right Sidebar: Progress + Reward */}
          <div className="space-y-6">
            {/* Progress Ring */}
            <motion.section
              className="p-8"
              style={{
                background: '#FFFFFF',
                border: '2px solid rgba(11,26,51,0.06)',
                borderRadius: 22,
                boxShadow: '0px 2px 0px rgba(11,26,51,0.04), 0px 8px 20px rgba(11,26,51,0.06)',
              }}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}
            >
              <h2 className="text-xs uppercase tracking-[0.2em] mb-6 text-center" style={{ ...FREDOKA, color: '#0065F8', fontWeight: 800 }}>
                Challenge Progress
              </h2>
              <div className="flex flex-col items-center">
                <div className="relative w-36 h-36 flex items-center justify-center mb-5">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 140 140">
                    <circle cx="70" cy="70" r="60" fill="transparent" stroke="rgba(11,26,51,0.08)" strokeWidth="10" />
                    <circle
                      cx="70" cy="70" r="60" fill="transparent"
                      stroke="#0065F8" strokeWidth="10"
                      strokeDasharray={circumference} strokeDashoffset={strokeOffset} strokeLinecap="round"
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute flex flex-col items-center">
                    <span className="text-3xl" style={{ ...FREDOKA, color: '#0B1A33', fontWeight: 800 }}>
                      {safeProgress}/{stories.length}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest" style={{ ...FREDOKA, color: '#5A6B85', fontWeight: 700 }}>
                      Stories
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  {stories.map((_, i) => (
                    <div
                      key={i}
                      className="w-3 h-3 rounded-full transition-all"
                      style={{
                        background: i < safeProgress ? '#0065F8' : 'rgba(11,26,51,0.10)',
                        boxShadow: i < safeProgress ? '0px 2px 0px #073B8E' : 'none',
                      }}
                    />
                  ))}
                </div>
              </div>
            </motion.section>

            {/* Reward Card */}
            <motion.section
              className="relative group p-8 overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, #FFFFFF 0%, #F5FAFF 100%)',
                border: '2px solid rgba(0,101,248,0.18)',
                borderRadius: 22,
                boxShadow: '0px 2px 0px rgba(11,26,51,0.04), 0px 8px 20px rgba(11,26,51,0.06)',
              }}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.7 }}
            >
              <div className="absolute -right-4 -bottom-4 opacity-15 group-hover:scale-110 transition-transform duration-700 pointer-events-none">
                <span style={{ fontSize: 100 }}>🎁</span>
              </div>
              <div className="relative z-10 flex flex-col items-center text-center">
                <div
                  className="w-16 h-16 flex items-center justify-center mb-4"
                  style={{
                    background: 'rgba(0,101,248,0.10)',
                    borderRadius: 18,
                    boxShadow: '0px 3px 0px rgba(11,26,51,0.06)',
                  }}
                >
                  <img src="/assets/pixley_stars.png" alt="" className="w-8 h-8" />
                </div>
                <h3 className="text-sm uppercase tracking-widest mb-1" style={{ ...FREDOKA, color: '#5A6B85', fontWeight: 800 }}>
                  Reward
                </h3>
                <p className="text-3xl mb-3" style={{ ...FREDOKA, color: '#0065F8', fontWeight: 800 }}>
                  {rewardStars} STARS
                </p>
                <p className="text-sm" style={{ ...FREDOKA, color: '#5A6B85' }}>
                  Finish all stories by Monday to claim your stars.
                </p>
              </div>
            </motion.section>
          </div>
        </div>
      </div>

      <ChallengeCompletionModal isOpen={showCompletionModal} onClose={() => setShowCompletionModal(false)} rewardStars={rewardStars} />
    </div>
  );
};

export default WeeklyChallengePage;
